import React, { useEffect, useState } from "react";
import Select from "react-select";

export type Option = {
  value: string;
  label: string;
  description?: string;
};

export interface SelectProps {
  options: Option[];
  defaultValue?: Option;
  value?: Option | null;
  className?: string;
  styles?: any;
  onValueChange?: (value: string) => void;
  onChange?: (option: Option | null) => void;
  required?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
}

const CommonSelect: React.FC<SelectProps> = ({
  options,
  defaultValue,
  value,
  className,
  onValueChange,
  onChange,
  required,
  placeholder = "Select",
  isDisabled = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(
    value || defaultValue || null
  );

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: isDisabled ? "#e9ecef" : "white",
      cursor: isDisabled ? "not-allowed" : "pointer",
      opacity: isDisabled ? 0.65 : 1,
      borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      },
    }),
    option: (base: any, state: any) => ({
      ...base,
      color: "#6A7287",
      backgroundColor: state.isSelected ? "#ddd" : "white",
      cursor: isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        backgroundColor: isDisabled
          ? "white"
          : state.isFocused
          ? "#3D5EE1"
          : "blue",
        color: isDisabled ? "#6A7287" : state.isFocused ? "#fff" : "#6A7287",
        cursor: isDisabled ? "not-allowed" : "pointer",
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      cursor: isDisabled ? "not-allowed" : "pointer",
    }),
    placeholder: (base: any) => ({
      ...base,
      cursor: isDisabled ? "not-allowed" : "pointer",
    }),
    input: (base: any) => ({
      ...base,
      cursor: isDisabled ? "not-allowed" : "text",
    }),
  };

  const formatOptionLabel = ({ label, description }: Option) => (
    <div>
      <div>{label}</div>
      {description && <small>{description}</small>}
    </div>
  );

  const handleChange = (option: Option | null) => {
    setSelectedOption(option);
    if (onValueChange) {
      onValueChange(option?.value || "");
    }
    if (onChange) {
      onChange(option);
    }
  };
  useEffect(() => {
    setSelectedOption(value || defaultValue || null);
  }, [value, defaultValue]);

  return (
    <div className={`select-wrapper ${required ? "required" : ""}`}>
      <Select
        classNamePrefix="react-select"
        className={className}
        styles={customStyles}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable={!required}
        formatOptionLabel={formatOptionLabel}
        isDisabled={isDisabled}
      />
      {required && !selectedOption && (
        <div className="invalid-feedback d-none">This field is required</div>
      )}
    </div>
  );
};

export default CommonSelect;
